import { EndPoint } from '@/enums/EndPoint'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import dayjs from 'dayjs'

/**
 * Converts an array of local weekday numbers (0 = Sunday, ..., 6 = Saturday)
 * to their UTC equivalents, based on the UTC/local day difference of a given ISO date.
 *
 * @param localDays - An array of weekday numbers in local time (0 = Sunday)
 * @param targetDate - ISO string (e.g. "2025-06-19T13:00:00Z") used to determine UTC offset
 * @returns Array of weekday numbers in UTC time (0 = Sunday)
 */
export const convertLocalDaysToUTC = (
  localDays: number[],
  targetDate: Date = new Date()
): number[] => {
  const localDay = targetDate.getDay()
  const utcDay = targetDate.getUTCDay()
  const offset = utcDay - localDay
  return localDays.map((localWeekday) => {
    const adjusted = (localWeekday + offset + 7) % 7
    return adjusted
  })
}

/**
 * Get the UTC time for the user's active data set accounting for the
 * relation between the the user's current time zone and the dataset time zone.
 * Wherever in the world the user is, the time will be the same as the dataset time zone.
 * @param date The date with the time to be replaced.
 * @param timeZone The time zone of the data set.
 * @returns An ISO time zone converted to the data set timezone.
 */
export const replaceTime = (date: Date, timeZone: string): string => {
  if (!isDefined(date) || !isDefined(timeZone)) {
    return ''
  }

  const localDate = dayjs(date).format('YYYY-MM-DD')
  const localTime = new Date().toTimeString().substring(0, 8)
  const newDateTime = new Date(`${localDate}T${localTime}`).toLocaleString('en-US', {
    timeZone: timeZone
  })
  return newDateTime
}

/**
 * Format bytes to a friendly format.
 * @param bytes Total bytes.
 * @param decimals Number of ceimals to report.
 * @returns
 */
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Formats the date with an optional time.
 * @param isoDate An ISO date string value.
 * @param includeTime Flag that indicates whether to include the time.
 * @returns
 */
export const formatDate = (
  isoDate: string | null | undefined,
  includeTime: boolean = false
): string => {
  const settingsStore = useSettingsStore()
  const locale = settingsStore.locale
  let dateValue = '-'
  if (isoDate === null || isoDate === undefined) {
    return dateValue
  }
  const localDate = new Date(isoDate)

  if (isDefined(localDate)) {
    dateValue = localDate.toLocaleDateString(locale?.language)
    if (includeTime) {
      const timeValue = localDate.toLocaleTimeString(locale?.language, { timeStyle: 'short' })
      dateValue += ` ${timeValue}`
    }
  }
  return dateValue
}

/**
 * Gets a date with the earliest local date/time possible for the date provided.
 * @param date The date, defaults to today.
 * @returns
 */
export const getEarliestDateTime = (date: Date = new Date()) => {
  return dayjs(`${dayjs(date).format('YYYY-MM-DD')}`)
}

/**
 * Gets a date with the latest local date/time possible for the date provided.
 * @param date The date, defaults to today.
 * @returns
 */
export const getLatestDateTime = (date: Date = new Date()) => {
  return dayjs(`${dayjs(date).format('YYYY-MM-DD')}T23:59:59.999`)
}

/**
 * Returns true if the value is defined.
 * @param value Any item value.
 * @param noEmptyObjects By default an empty object is defined.
 * @returns
 */
export const isDefined = (value: any, noEmptyObjects: boolean = false): boolean => {
  if (value === undefined || value === null) {
    return false
  }

  const type = typeof value
  switch (type) {
    case 'number':
    case 'bigint': {
      return isNaN(value) === false
    }
    case 'string': {
      return value.trim().length > 0
    }
    case 'object': {
      if (value.getTime) {
        return !isNaN(value.getTime())
      }
      if (noEmptyObjects) {
        return Object.keys(value).length > 0
      }
    }
  }
  return true
}

/**
 * Takes any object and converts all empty strings to null values.
 * @param item An object instance.
 */
export const nullifyEmptyStrings = (
  items: Array<Record<string, any>> | Record<string, any>,
  endpoint: EndPoint
): void => {
  // Allow fo exceptions!
  const excludes: Map<EndPoint, Array<string>> = new Map()
  excludes.set(EndPoint.Masks, ['approval'])
  if (!Array.isArray(items)) {
    items = [items]
  }
  items.forEach((item: Record<string, any>) => {
    const keys = Object.keys(item)
    keys.forEach((key: string) => {
      const excludedFields = excludes.get(endpoint)
      if (!excludedFields?.includes(key)) {
        if (typeof item[key] === 'string' && item[key].trim() === '') {
          item[key] = null
        }
      }
    })
  })
}

/**
 * Round to the nearest value using the rounding value as an increment.
 * @param value The value to be rounded.
 * @param roundingValue Establishes the nearest value.
 * @returns
 */
export const roundToNearest = (value: number, roundingValue: number): number => {
  if (roundingValue === 0) {
    throw new Error('Rounding value cannot be zero.')
  }
  return Math.round(value / roundingValue) * roundingValue
}

/**
 * Parse the date as a local date.
 * @param isoDate An ISO date string value.
 * @returns
 */
export const parseDateAsLocal = (isoDate: string): Date => {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Returns the value stripped fo symbols and lower cased.
 * @param value Any string value.
 * @returns
 */
export const sanitizeAlphaNumeric = (value: string): string => {
  if (!isDefined(value)) {
    return ''
  }
  value = value.trim().toLowerCase()
  value = value.replace(/[^a-zA-Z0-9]/g, '')
  return value
}
